import { useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { Star, ChevronLeft, ChevronRight } from "@/components/Icon";

type Review = {
  name: string;
  date: string;
  text: string;
  initial?: string;
};

// Handmatig gecureerde 5-sterren Google reviews van Magic Print Textieldruk.
// Uitbreiden? Voeg simpelweg een object toe aan de lijst.
const reviews: Review[] = [
  {
    name: "Sander de Vries",
    date: "2 maanden geleden",
    text: "Super snelle service en de kwaliteit van de bedrukking is top! Onze bedrijfsshirts zien er professioneel uit. Absoluut een aanrader.",
  },
  {
    name: "Linda Bakker",
    date: "3 maanden geleden",
    text: "Fijne persoonlijke aanpak en meedenken bij het ontwerp. De hoodies voor ons team zijn prachtig geworden. Bedankt Magic Print!",
  },
  {
    name: "Mark Jansen",
    date: "4 maanden geleden",
    text: "Snelle levering, scherpe prijs en de kwaliteit is uitstekend. Wij bestellen al jaren onze werkkleding bij Magic Print.",
  },
  {
    name: "Fatima el Amrani",
    date: "5 maanden geleden",
    text: "Prima communicatie en het eindresultaat overtrof de verwachtingen. Zeer tevreden met onze bedrukte polo's.",
  },
  {
    name: "Peter van Leeuwen",
    date: "6 maanden geleden",
    text: "Uitstekende service en vakmanschap. De borduring van ons logo is haarscherp. Zeker weer een volgende keer!",
  },
  {
    name: "Anouk Smit",
    date: "7 maanden geleden",
    text: "Wat een topbedrijf! Snelle offerte, meedenken over materiaal en druktechniek, en netjes op tijd geleverd. 5 sterren verdiend.",
  },
  {
    name: "Jeroen Mulder",
    date: "8 maanden geleden",
    text: "Voor onze sportvereniging shirts laten drukken. Perfect resultaat, mooie kleuren en de prints blijven ook na wassen goed zitten.",
  },
  {
    name: "Denise Kok",
    date: "10 maanden geleden",
    text: "Vriendelijke mensen, professioneel werk en scherpe prijzen. Onze evenementshirts zagen er geweldig uit.",
  },
];

const GoogleLogo = () => (
  <svg viewBox="0 0 48 48" className="w-5 h-5" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
);

const Stars = () => (
  <div className="flex gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} className="w-4 h-4 fill-[#FBBC05] text-[#FBBC05]" />
    ))}
  </div>
);

const GoogleReviews = () => {
  // Embla mutates DOM styles/transforms via its ref callback synchronously on
  // mount. That produces a different DOM than the initial React render, which
  // breaks prerender hydration (React errors #418/#423). Defer initializing
  // embla until after the first client render so the hydration snapshot
  // matches the server-rendered HTML exactly.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    if (typeof window !== "undefined" && (window as unknown as { __IS_PRERENDER__?: boolean }).__IS_PRERENDER__) {
      return;
    }
    setMounted(true);
  }, []);

  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: "start" },
    [Autoplay({ delay: 5000, stopOnInteraction: false })]
  );
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  useEffect(() => {
    if (!emblaApi) return;
    const update = () => {
      setCanPrev(emblaApi.canScrollPrev());
      setCanNext(emblaApi.canScrollNext());
    };
    update();
    emblaApi.on("select", update);
    emblaApi.on("reInit", update);
  }, [emblaApi]);

  return (
    <section className="py-20 md:py-28 bg-[#fcfcfc]">
      <div className="container">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 rounded-full bg-secondary">
            <GoogleLogo />
            <span className="text-sm font-medium">Google reviews</span>
            <Stars />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-[#0a2933]">
            Wat onze klanten zeggen
          </h2>
          <p className="text-muted-foreground">
            Duizenden tevreden klanten gingen u voor. Lees hun ervaringen met Magic Print.
          </p>
        </div>

        <div className="relative">
          <div className="overflow-hidden" ref={mounted ? emblaRef : undefined}>
            <div className="flex gap-6">
              {reviews.map((r, i) => (
                <div
                  key={i}
                  className="flex-[0_0_100%] md:flex-[0_0_50%] lg:flex-[0_0_33.333%] min-w-0"
                >
                  <article className="h-full bg-card border border-border rounded-2xl p-6 flex flex-col gap-4 hover:border-primary/40 hover:shadow-md transition-all">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 text-primary font-semibold flex items-center justify-center">
                          {r.name.charAt(0)}
                        </div>
                        <div>
                          <div className="font-medium text-sm text-[#0a2933]">{r.name}</div>
                          <div className="text-xs text-muted-foreground">{r.date}</div>
                        </div>
                      </div>
                      <GoogleLogo />
                    </div>
                    <Stars />
                    <p className="text-sm text-muted-foreground leading-relaxed flex-1">
                      "{r.text}"
                    </p>
                  </article>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-center items-center gap-3 mt-8">
            <button
              type="button"
              onClick={() => emblaApi?.scrollPrev()}
              disabled={!canPrev}
              aria-label="Vorige review"
              className="w-10 h-10 rounded-full border border-border bg-card flex items-center justify-center hover:border-primary/40 hover:text-primary transition-colors disabled:opacity-50"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <a
              href="https://www.google.com/search?q=Magic+Print+Textieldruk+Reviews"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-primary hover:underline"
            >
              Bekijk alle reviews op Google
            </a>
            <button
              type="button"
              onClick={() => emblaApi?.scrollNext()}
              disabled={!canNext}
              aria-label="Volgende review"
              className="w-10 h-10 rounded-full border border-border bg-card flex items-center justify-center hover:border-primary/40 hover:text-primary transition-colors disabled:opacity-50"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GoogleReviews;
